/* eslint-disable consistent-return */
/* eslint-disable no-nested-ternary */

import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  VictoryChart,
  VictoryAxis,
  VictoryContainer,
  VictoryTooltip,
  VictoryLine,
  VictoryScatter,
  VictoryBar,
} from "victory";

import MDBox from "components/MDBox";
import lightColors from "assets/theme/base/colors";
import darkColors from "assets/theme-dark/base/colors";
import helpers from "../AnalyticsHelpers";

function AnalyticsChart({ points, measurement, field, darkMode, metricsConfig, queryPeriod, t }) {
  const isPresence = measurement === "presence";
  const showColumns = isPresence && field === "value";
  const measurementSuffix = helpers.getMetricSuffix(measurement, metricsConfig);

  const isIncremental = helpers.isIncrementalMetric(measurement);
  const showStateInTooltip = isIncremental && field === "diff" && queryPeriod === "hourly";

  const wrapRef = useRef(null);
  const [width, setWidth] = useState(800);
  const [height, setHeight] = useState(420);

  useEffect(() => {
    if (!wrapRef.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      const { width: w, height: h } = entry.contentRect;
      if (w) setWidth(w);
      if (h) setHeight(Math.max(300, h));
    });
    ro.observe(wrapRef.current);
    const disconnect = () => ro.disconnect();
    return disconnect;
  }, []);

  const { series, ticks } = useMemo(() => helpers.buildChartModel(points), [points]);

  if (!series.length) return null;

  const yValueForPresenceBarIfIsOne = 2;

  const dataForBars = series.map((data) => ({
    ...data,
    displayY: data?.y === 0 ? 0.05 : yValueForPresenceBarIfIsOne,
  }));

  const yValues = series.map((data) => data?.y);

  const yMin = Math.min(...yValues);
  let yMax = Math.max(...yValues);

  if (yMax === yMin) {
    yMax = yMin + 1;
  }

  const yRange = yMax - yMin;
  const yPadding = Math.max(yRange * 0.05, 0.05);

  const yDomain = [yMin - yPadding, yMax + yPadding];
  const { xDomainPadding, yDomainPadding } = helpers.getAxisDomainPadding(series.length);

  const activeColors = darkMode ? darkColors : lightColors;
  const baseColor = activeColors.info.main;
  const chartModeColors = darkMode
    ? {
        axis: activeColors.white.main,
        grid: activeColors.dark.focus,
        tooltipFill: activeColors.background.card,
        tooltipStroke: activeColors.dark.focus,
        tooltipText: activeColors.white.main,
      }
    : {
        axis: activeColors.grey[600],
        grid: activeColors.grey[300],
        tooltipFill: activeColors.white.main,
        tooltipStroke: activeColors.grey[300],
        tooltipText: activeColors.dark.main,
      };

  return (
    <MDBox
      sx={{
        width: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        mt: 1,
        mb: 1,
      }}
    >
      <MDBox
        ref={wrapRef}
        sx={{
          width: Math.max(width, series.length * 60),
          minWidth: "800px",
          height: "430px",
        }}
      >
        <VictoryChart
          width={width}
          height={height}
          padding={{ top: 40, bottom: 64, left: 70, right: 40 }}
          domain={{ y: yDomain }}
          domainPadding={{
            x: xDomainPadding,
            y: yDomainPadding,
          }}
          animate={{ duration: 400 }}
          containerComponent={<VictoryContainer activateData />}
        >
          {/* Eje Y */}
          <VictoryAxis
            dependentAxis
            style={{
              axis: { stroke: chartModeColors.axis },
              tickLabels: { fill: chartModeColors.axis, fontSize: 11 },
              grid: { stroke: chartModeColors.grid },
            }}
            tickFormat={isPresence && field === "value" ? () => "" : (tickValue) => tickValue}
          />
          {/* Eje X */}
          <VictoryAxis
            tickValues={ticks}
            tickFormat={(tickValue) => series.find((point) => point.x === tickValue)?.xLabel}
            style={{
              axis: { stroke: chartModeColors.axis },
              tickLabels: {
                fill: chartModeColors.axis,
                fontSize: 12,
                textAnchor: "middle",
              },
            }}
          />

          {/* Barras */}
          {showColumns && (
            <VictoryBar
              data={dataForBars}
              y="displayY"
              style={{
                data: {
                  fill: ({ datum }) =>
                    isPresence && field === "value"
                      ? datum.y === 0
                        ? activeColors.error.main
                        : baseColor
                      : baseColor,
                },
              }}
              barWidth={Math.max(6, Math.min(width / series.length / 2, 20))}
              labels={({ datum }) =>
                helpers.formatAnalyticsTooltipLabel({
                  toolTip: datum,
                  showStateInTooltip,
                  measurement,
                  field,
                  measurementSuffix,
                  t,
                })
              }
              labelComponent={
                <VictoryTooltip
                  flyoutStyle={{
                    fill: chartModeColors.tooltipFill,
                    stroke: chartModeColors.tooltipStroke,
                    strokeWidth: 1,
                  }}
                  flyoutPadding={6}
                  pointerLength={4}
                  constrainToVisibleArea
                  style={{
                    fontSize: 18,
                    fill: chartModeColors.tooltipText,
                    fontWeight: 600,
                  }}
                />
              }
            />
          )}

          {!showColumns && (
            <VictoryLine
              data={series}
              y="y"
              style={{
                data: {
                  stroke: baseColor,
                  strokeWidth: 3,
                },
              }}
            />
          )}
          {!showColumns && (
            <VictoryScatter
              data={series}
              y="y"
              size={3}
              style={{
                data: {
                  fill: ({ datum }) =>
                    helpers.isRecoverTooltipState({ toolTip: datum, showStateInTooltip })
                      ? activeColors.warning.main
                      : baseColor,
                },
              }}
              labels={({ datum }) =>
                helpers.formatAnalyticsTooltipLabel({
                  toolTip: datum,
                  showStateInTooltip,
                  measurement,
                  field,
                  measurementSuffix,
                  t,
                })
              }
              labelComponent={
                <VictoryTooltip
                  flyoutStyle={{
                    fill: chartModeColors.tooltipFill,
                    stroke: chartModeColors.tooltipStroke,
                    strokeWidth: 1,
                  }}
                  flyoutPadding={6}
                  pointerLength={4}
                  constrainToVisibleArea
                  style={{
                    fontSize: 14,
                    fill: chartModeColors.tooltipText,
                  }}
                />
              }
            />
          )}
        </VictoryChart>
      </MDBox>
    </MDBox>
  );
}

AnalyticsChart.propTypes = {
  points: PropTypes.array.isRequired,
  darkMode: PropTypes.bool,
  measurement: PropTypes.string,
  field: PropTypes.string,
  metricsConfig: PropTypes.object,
  queryPeriod: PropTypes.string,
  t: PropTypes.func,
};

AnalyticsChart.defaultProps = {
  darkMode: false,
  measurement: "",
  field: "",
  metricsConfig: {},
  queryPeriod: "",
  t: () => {},
};

export default AnalyticsChart;
